import { StickerCard } from "@/components/sticker-card/StickerCard.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { FeedbackScoreCardProps } from "@/pages/recruit/scorecards/feedback/scorecards-feedback.ts";
import { useCandidatesByApplicant } from "@/queries/recruit/use-candidates.query.ts";
import { useJobDetails } from "@/queries/recruit/use-jobs.query.ts";
import { scoreCardsKeys } from "@/queries/recruit/use-scorecards.query.ts";
import { getJobProfileTitle } from "@/types/job-profile.types.ts";
import { ApiScoreCardStatus } from "@/types/recruit/scorecard.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { Button, FullScreenPage, Stack, Text, Title } from "@skailer-com/uikit";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffectOnce } from "react-use";

export function FeedbackScoreCardFinal({ feedback, setForcedState }: FeedbackScoreCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canIEdit } = usePermissions();
  const returnToCandidates = !feedback.applicant && canIEdit(ApiPermission.recruiting_module);
  const candidateName = feedback.applicant
    ? `${feedback.applicant.first_name} ${feedback.applicant.last_name}`
    : "";
  const { data: candidates } = useCandidatesByApplicant(feedback.applicant?.id);
  const queryClient = useQueryClient();

  useEffectOnce(() => {
    queryClient.invalidateQueries({
      queryKey: scoreCardsKeys.candidateFeedbacksOverview(feedback.candidate_id).queryKey,
    });
  });

  const currentCandidate = useMemo(() => {
    return candidates?.find((candidate) => candidate.id === feedback.candidate_id);
  }, [candidates, feedback.candidate_id]);

  const { data: job } = useJobDetails(currentCandidate?.job.id);

  const jobInfo = useMemo(() => {
    if (!job) return "";

    const jobProfileTitle = job.job_profile ? getJobProfileTitle(job.job_profile) : "";

    return [jobProfileTitle, job.title].filter(Boolean).join(" ");
  }, [job]);

  return (
    <FullScreenPage contentSize={"lg"}>
      <Stack direction={"horizontal"} items={"center"}>
        <StickerCard
          title={candidateName}
          caption={feedback.scorecard.title}
          avatar_url={feedback.applicant?.photo_url}
          contentSlot={<Text>{t("ats:candidate_for", { job_info: jobInfo })}</Text>}
        />
        <Stack className={"pl-18"} gap={"lg"}>
          <Title
            size={"lg"}
            header={t("scorecards:feedback_sent", {
              candidate_name: candidateName,
            })}
          ></Title>
          <Stack direction={"horizontal"} gap={"sm"}>
            {canIEdit(ApiPermission.recruiting_module) && (
              <Button
                size={"lg"}
                onClick={() => {
                  if (returnToCandidates) {
                    navigate("/recruitment/candidates/");
                    return;
                  }
                  const jobId = feedback.job?.id ?? currentCandidate?.job.id;
                  const search = jobId ? `?job_id=${jobId}` : "";
                  navigate(
                    feedback.applicant
                      ? `/recruitment/candidates/${feedback.applicant.id}/${search}`
                      : `/recruitment/candidates/${search}`
                  );
                }}
              >
                {t(
                  returnToCandidates
                    ? "scorecards:return_to_candidates"
                    : "scorecards:return_to_candidate"
                )}
              </Button>
            )}
            <Button
              variant={"secondary"}
              size={"lg"}
              onClick={async () => {
                if (setForcedState) setForcedState(ApiScoreCardStatus.in_progress);
              }}
            >
              {t("scorecards:change_answers")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </FullScreenPage>
  );
}
